import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, Group, Text } from "@mantine/core";
import { IconPlayerPlay } from "@tabler/icons-react";
import moment from "moment";
import "moment/locale/hy-am";
import "moment/locale/ru";
import { T } from "@/helpers/translator";
import styles from "./page.module.css";

const EMPTY_SELECTION = new Set();

/**
 * Keep the real image URL out of the DOM until the tile enters the viewport.
 * Native `loading="lazy"` is retained as a second line of defence, but browsers
 * are allowed to fetch native-lazy images well before they are visible.
 */
export const ViewportImage = ({ photo, onNaturalSize }) => {
	const imageRef = useRef(null);
	const [inViewport, setInViewport] = useState(() => typeof IntersectionObserver === "undefined");

	useEffect(() => {
		if (inViewport || !imageRef.current) return undefined;
		const observer = new IntersectionObserver(([entry]) => {
			if (!entry.isIntersecting) return;
			setInViewport(true);
			observer.disconnect();
		}, { rootMargin: "0px", threshold: 0.01 });
		observer.observe(imageRef.current);
		return () => observer.disconnect();
	}, [inViewport]);

	return (
		<img
			ref={imageRef}
			src={inViewport ? photo.src : undefined}
			alt={photo.alt || photo.title || ""}
			loading="lazy"
			decoding="async"
			onLoad={(event) => {
				const { naturalWidth, naturalHeight } = event.currentTarget;
				if (naturalWidth > 0 && naturalHeight > 0)
				{
					onNaturalSize?.(naturalWidth, naturalHeight);
				}
			}}
			onError={(event) => {
				const image = event.currentTarget;
				if (!photo.fallbackSrc || image.dataset.usedFallback === "true") return;
				image.dataset.usedFallback = "true";
				image.src = photo.fallbackSrc;
			}}
		/>
	);
};

const getDayKey = (date) => (
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
);

const formatDayHeading = (date, lang) => {
	const day = moment(date);
	const today = moment().startOf("day");
	if (day.isSame(today, "day")) return T("today", lang) || "Today";
	if (day.isSame(today.clone().subtract(1, "day"), "day")) return T("yesterday", lang) || "Yesterday";
	return day.format(day.year() === today.year() ? "ddd, MMM D" : "ddd, MMM D, YYYY");
};

const validPhotoDate = (photo) => {
	const date = photo?.date instanceof Date ? photo.date : new Date(photo?.date || 0);
	return Number.isNaN(date.getTime()) ? new Date(0) : date;
};

export const groupMediaByMonth = (photos = []) => {
	const groups = photos.reduce((result, photo) => {
		const date = validPhotoDate(photo);
		const year = date.getFullYear();
		const month = date.getMonth();
		const key = `${year}-${month}`;
		if (!result[key]) result[key] = { year, month, photos: [], date: new Date(year, month) };
		result[key].photos.push(photo);
		return result;
	}, {});
	return Object.values(groups).sort((a, b) => b.date - a.date);
};

/**
 * Shared date-organized media view used by the main Gallery and filtered galleries.
 * `initialPhotoCount`/`batchSize` enable progressive rendering for large collections.
 */
export const OrganizedMediaGallery = ({
	photos = [],
	lang = "en",
	selectedIds = EMPTY_SELECTION,
	selectable = false,
	onPhotoClick,
	onTogglePhotoSelection,
	onSetPhotosSelected,
	initialPhotoCount,
	batchSize = 24,
	resetKey
}) => {
	const progressive = Number.isFinite(initialPhotoCount) && initialPhotoCount > 0;
	const [visibleCount, setVisibleCount] = useState(progressive ? initialPhotoCount : photos.length);
	const [naturalAspectRatios, setNaturalAspectRatios] = useState({});
	const loadMoreRef = useRef(null);
	const recordNaturalAspectRatio = useCallback((photoKey, width, height) => {
		const aspectRatio = width / height;
		setNaturalAspectRatios((current) => (
			current[photoKey] === aspectRatio
				? current
				: { ...current, [photoKey]: aspectRatio }
		));
	}, []);

	useEffect(() => {
		moment.locale(lang === "hy" ? "hy-am" : lang);
	}, [lang]);

	useEffect(() => {
		setVisibleCount(progressive ? initialPhotoCount : photos.length);
	}, [initialPhotoCount, photos.length, progressive, resetKey]);

	const orderedPhotos = useMemo(() => (
		[...photos].sort((a, b) => validPhotoDate(b) - validPhotoDate(a))
	), [photos]);
	const visiblePhotos = progressive ? orderedPhotos.slice(0, visibleCount) : orderedPhotos;
	const monthGroups = useMemo(() => groupMediaByMonth(visiblePhotos), [visiblePhotos]);

	useEffect(() => {
		const sentinel = loadMoreRef.current;
		if (!progressive || !sentinel || visibleCount >= orderedPhotos.length) return undefined;
		const observer = new IntersectionObserver(([entry]) => {
			if (!entry.isIntersecting) return;
			setVisibleCount((count) => Math.min(count + batchSize, orderedPhotos.length));
		}, { rootMargin: "0px" });
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [batchSize, orderedPhotos.length, progressive, visibleCount]);

	const activatePhoto = (event, index, photoList) => {
		if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") return;
		if (event.type === "keydown") event.preventDefault();
		onPhotoClick?.(index, photoList);
	};

	return (
		<>
			{monthGroups.map((monthGroup) => {
				const sectionId = `month-${monthGroup.year}-${monthGroup.month}`;
				const photosByDate = monthGroup.photos.reduce((groups, photo) => {
					const date = validPhotoDate(photo);
					const dateKey = getDayKey(date);
					if (!groups[dateKey]) groups[dateKey] = { date, photos: [] };
					groups[dateKey].photos.push(photo);
					return groups;
				}, {});
				const sortedDates = Object.entries(photosByDate).sort((a, b) => b[1].date - a[1].date);

				return (
					<Box key={sectionId} id={sectionId} className={styles.monthSection}>
						<Text size="2xl" weight={700} className={styles.monthHeading}>
							{moment().month(monthGroup.month).format("MMMM")} {monthGroup.year}
						</Text>
						{sortedDates.map(([dateKey, group]) => {
							const dayPhotoIds = group.photos.map((photo) => photo.id);
							const daySelected = selectable && dayPhotoIds.every((id) => selectedIds.has(id));
							const dayPartiallySelected = selectable && dayPhotoIds.some((id) => selectedIds.has(id));

							return (
								<Box key={dateKey} className={styles.daySection}>
									<Group spacing="sm" className={styles.dayHeading}>
										{selectable && (
											<Checkbox
												aria-label={`Select all items from ${formatDayHeading(group.date, lang)}`}
												checked={daySelected}
												indeterminate={dayPartiallySelected && !daySelected}
												onChange={(event) => onSetPhotosSelected?.(dayPhotoIds, event.currentTarget.checked)}
											/>
										)}
										<Text size="lg" weight={600} color="dimmed">
											{formatDayHeading(group.date, lang)}
										</Text>
									</Group>
									<div className={styles.naturalPhotoRow}>
										{group.photos.map((photo, index) => {
											const photoKey = photo.id || photo.key || `${dateKey}-${index}`;
											const aspectRatio = naturalAspectRatios[photoKey] ||
												(Number(photo.width) || 4) / (Number(photo.height) || 3);

											return (
											<div
												key={photoKey}
												className={`${styles.galleryAlbumPhotoWrapper} ${selectedIds.has(photo.id) ? styles.selectedPhoto : ""}`}
												style={{ aspectRatio }}
												role="button"
												tabIndex={0}
												onClick={(event) => activatePhoto(event, index, group.photos)}
												onKeyDown={(event) => activatePhoto(event, index, group.photos)}
											>
												<ViewportImage
													photo={photo}
													onNaturalSize={(width, height) => recordNaturalAspectRatio(photoKey, width, height)}
												/>
												{photo.highlightBox && (
													<div
														className={styles.faceHighlightBox}
														style={{
															left: `${photo.highlightBox.x * 100}%`,
															top: `${photo.highlightBox.y * 100}%`,
															width: `${photo.highlightBox.width * 100}%`,
															height: `${photo.highlightBox.height * 100}%`
														}}
														aria-hidden="true"
													/>
												)}
												{selectable && (
													<div className={styles.photoCheckbox} onClick={(event) => event.stopPropagation()}>
														<Checkbox
															aria-label={`Select ${photo.title}`}
															checked={selectedIds.has(photo.id)}
															onChange={() => onTogglePhotoSelection?.(photo.id)}
														/>
													</div>
												)}
												{photo?.mediaType === "video" && (
													<div className={styles.videoPlayBadge} aria-hidden="true">
														<IconPlayerPlay size={18} />
													</div>
												)}
											</div>
											);
										})}
									</div>
								</Box>
							);
						})}
					</Box>
				);
			})}
			{progressive && visibleCount < orderedPhotos.length && (
				<div ref={loadMoreRef} className={styles.loadMoreSentinel} aria-hidden="true" />
			)}
		</>
	);
};

export default OrganizedMediaGallery;
